import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import Session from "users/__session/session";
import { ProposalBoxType, ProposalContext } from "work/proposals/__constants/constants";
import {
  SaveProposalAPIRequest,
  ShareProposalAPIRequest,
} from "work/proposals/_api/commands/update-proposal-api-request";
import WorkProposalAPIRequest from "work/proposals/_api/commands/work-proposal-api-request";
import ProposalQuery from "work/proposals/_api/queries/proposal-query";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import { ProposalBuilderJSON } from "work/proposals/_schemas/proposal-builder-schema";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import Proposal from "work/proposals/proposal";
import z from "zod";

export const proposalsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getProposals: build.query<Paginated<ProposalJSON>, { query: ProposalQuery; interfaceName: string }>({
      query: ({ query }) => ({
        url: "work/proposals",
        params: query?.searchParams,
      }),
      rawResponseSchema: z.array(ProposalSchema.ProposalJSONZ),
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        const { data: proposalsResponse } = await queryFulfilled;
        const session = Session.loadFromStorage(() => {});
        proposalsResponse.data.forEach((proposalJSON) => {
          if (!proposalJSON.id) return;
          dispatch(
            proposalsApi.util.upsertQueryData(
              "getProposalById",
              { proposalId: proposalJSON.id, session },
              proposalJSON,
            ),
          );
        });
      },
      transformResponse: (response: ProposalJSON[], meta): Paginated<ProposalJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
              // Tag each item
              ...(result?.data?.map((item) => ({
                type: "Proposal" as const,
                id: item.id ?? undefined,
              })) ?? []),
              // Tag the list itself
              { type: "Proposal" as const, id: "LIST" },
            ]
          : [{ type: "Proposal" as const, id: "LIST" }],
    }),
    getProposalById: build.query<ProposalJSON | null, { proposalId: string; session: Session }>({
      query: ({ proposalId }) => `work/proposals/${proposalId}`,
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      providesTags: (result, error, request) => [{ type: "Proposal", id: request.proposalId }],
    }),
    getProposalsByIds: build.query<BarebonesProposalResponse[], { proposalIds: string[] }>({
      query: ({ proposalIds }) => ({
        url: "work/proposals/get-by-ids",
        params: proposalIds,
      }),
      providesTags: (result) =>
        result
          ? [
              // Tag each item
              ...(result?.map((item) => ({
                type: "Proposal" as const,
                id: item.id ?? undefined,
              })) ?? []),
              // Tag the list itself
              { type: "Proposal" as const, id: "LIST" },
            ]
          : [{ type: "Proposal" as const, id: "LIST" }],
    }),
    // #endregion

    // #region Counts
    getDraftProposalsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work/proposals",
        params: new URLSearchParams({
          boxType: ProposalBoxType.Draft,
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      rawResponseSchema: z.array(ProposalSchema.ProposalJSONZ),
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "Proposal" as const, id: "DraftCount" }],
    }),
    getActiveProposalsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work/proposals",
        params: new URLSearchParams({
          boxType: ProposalBoxType.AllActive,
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      rawResponseSchema: z.array(ProposalSchema.ProposalJSONZ),
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "Proposal" as const, id: "ActiveCount" }],
    }),
    getArchivedProposalsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work/proposals",
        params: new URLSearchParams({
          boxType: ProposalBoxType.Archive,
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      rawResponseSchema: z.array(ProposalSchema.ProposalJSONZ),
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "Proposal" as const, id: "ArchiveCount" }],
    }),
    // #endregion

    // #region Create
    createProposal: build.mutation<ProposalJSON, { proposal: Proposal; session: Session }>({
      query: ({ proposal, session }) => {
        const request = new WorkProposalAPIRequest(proposal, session);
        return {
          url: "work/proposals",
          method: "POST",
          body: request.payload,
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
      ],
    }),
    // #endregion

    // #region Save
    saveProposal: build.mutation<
      ProposalJSON,
      { updatedProposal: Proposal; proposalBuilderJSON?: ProposalBuilderJSON; session: Session }
    >({
      query: ({ updatedProposal, proposalBuilderJSON, session }) => {
        const request = new SaveProposalAPIRequest({
          updatedProposal,
          builderJSON: proposalBuilderJSON,
          session: session,
        });
        return {
          url: `work/proposals/${updatedProposal.id?.value}`,
          method: "PATCH",
          body: request.payload,
        };
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.updatedProposal.id?.value },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion

    // #region Share
    shareProposal: build.mutation<ProposalJSON, { proposal: Proposal; session: Session }>({
      query: ({ proposal, session }) => {
        const request = new ShareProposalAPIRequest({ proposal, session });
        return {
          url: `work/proposals/${proposal.id?.value}`,
          method: "PATCH",
          body: request.payload,
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposal.id?.value },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion

    // #region Delete
    deleteProposal: build.mutation<void, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Proposal", id: id.proposalId },
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
      ],
    }),
    // #endregion

    // #region Delete Drafts
    bulkDeleteProposals: build.mutation<void, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `work/draft-proposals`,
        method: "DELETE",
        body: ids,
      }),
      invalidatesTags: (_result, _error, args) => [
        ...args.ids.map((id) => ({ type: "Proposal" as const, id })),
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
      ],
    }),
    // #endregion

    // #region Cancel
    cancelProposal: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/cancel`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposalId },
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchivedCount" },
      ],
    }),
    // #endregion

    // #region Hire
    hireProposal: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/hire`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposalId },
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchivedCount" },
      ],
    }),
    // #endregion

    // #region Submit
    submitProposal: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/submit`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposalId },
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
        { type: "Proposal", id: "ActiveCount" },
      ],
    }),
    // #endregion

    // #region Approve
    approveProposal: build.mutation<ProposalJSON, { proposalId: string; session: Session }>({
      query: ({ proposalId }) => {
        if (!proposalId) {
          throw new Error("Proposal ID is required for approval");
        }
        return {
          url: `work/proposals/${proposalId}/approve`,
          method: "POST",
          body: {},
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onCacheEntryAdded(arg, api) {
        const entry = api.getCacheEntry();
        if (!entry.data) {
          console.warn("No proposal data found in cache for approval update");
          return;
        }
        api.dispatch(proposalsApi.util.upsertQueryData("getProposalById", arg, entry.data));
      },
      invalidatesTags: [{ type: "Proposal", id: "LIST" }],
    }),
    // #endregion

    // #region Reject
    rejectProposal: build.mutation<ProposalJSON, { proposalId?: string }>({
      query: ({ proposalId }) => {
        if (!proposalId) {
          throw new Error("Proposal ID is required for rejection");
        }
        return {
          url: `work/proposals/${proposalId}/reject`,
          method: "POST",
          body: {},
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposalId },
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchivedCount" },
      ],
    }),
    // #endregion

    // #region Revision
    requestProposalRevision: build.mutation<ProposalJSON, { proposal: Proposal; session: Session }>({
      query: ({ proposal, session }) => {
        if (!proposal.redlinedRevision) {
          throw new Error("No redlined revision found on proposal");
        }
        const request = new WorkProposalAPIRequest(proposal.redlinedRevision, session);
        return {
          url: `work/proposals/${proposal.id?.value}/request-revision`,
          method: "POST",
          body: request.payload,
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposal.id?.value },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion

    // #region Give Reviewer Approval
    giveReviewerApproval: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/reviewer-approve`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposalId },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion

    // #region Quit Team
    quitProposalTeam: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/quit-team`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.proposalId },
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchivedCount" },
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetProposalsQuery,
  useGetProposalByIdQuery,
  useGetProposalsByIdsQuery,
  useGetDraftProposalsCountQuery,
  useGetActiveProposalsCountQuery,
  useGetArchivedProposalsCountQuery,
  useCreateProposalMutation,
  useSaveProposalMutation,
  useShareProposalMutation,
  useDeleteProposalMutation,
  useBulkDeleteProposalsMutation,
  useCancelProposalMutation,
  useHireProposalMutation,
  useSubmitProposalMutation,
  useApproveProposalMutation,
  useRejectProposalMutation,
  useRequestProposalRevisionMutation,
  useGiveReviewerApprovalMutation,
  useQuitProposalTeamMutation,
} = proposalsApi;
